import Papa from "papaparse";
import type { Identifier } from "ra-core";

import type {
  Company,
  Contact,
  Project,
  ProjectImportIssue,
  ProjectImportRow,
  ProjectImportSummary,
} from "../types";

export const PROJECT_CSV_OPTIONAL_HEADERS = ["Referral/Partner Company"];

export const PROJECT_CSV_HEADERS = [
  "Source",
  "Job Type",
  "Customer Name",
  "Address",
  "Status",
  "Phone",
  "Email",
  "Removal Date",
  "JOB Date",
  "Reinstall Date",
  "Sub",
  "Sub Inv in shared inv drive?",
  "Qty",
  "Unit",
  "REVENUE/CONTRACT",
  "MATERIAL",
  "LABOR",
  "DUMP",
  "COMMISH/MARKETING",
  "OTHER",
  "Total Job Cost",
  "GROSS",
  "Margin",
  "Invoiced?",
  "Paid?",
  "Invoice Pay Date",
  "Codex Notes",
  "Human Note",
  "Date of Lead",
  "Calendar / Link",
  "Source Key",
];

export type ProjectCsvRow = Record<string, string | undefined>;

export type ProjectCsvParseResult = {
  data: ProjectCsvRow[];
  headers: string[];
  errors: Array<{ row?: number; message: string }>;
  missingHeaders: string[];
};

export type ExistingProjectIdentity = Pick<
  Project,
  "id" | "source_key" | "source_row_number" | "import_batch_id"
>;

export type ProjectImportPreviewOptions = {
  importBatchId?: string;
  existingProjects?: ExistingProjectIdentity[];
};

const NULL_VALUES = new Set(["", "na", "n/a", "none", "null", "-", "—"]);

export const normalizeText = (value: unknown): string =>
  value == null ? "" : String(value).replace(/\r\n/g, "\n").trim();

/**
 * PostgreSQL text/jsonb values cannot contain NUL or other C0 control bytes.
 * Keep the raw payload readable and loss-aware instead of letting one source
 * row abort an otherwise valid import. Printable source values are unchanged.
 */
export const sanitizeProjectStorageText = (value: unknown): string =>
  value == null
    ? ""
    : String(value).replace(
        // PostgreSQL text/jsonb rejects these source control bytes.
        // eslint-disable-next-line no-control-regex
        /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g,
        (character) =>
          `[U+${character.charCodeAt(0).toString(16).padStart(4, "0")}]`,
      );

export const sanitizeProjectRawRow = (row: ProjectCsvRow): ProjectCsvRow =>
  Object.fromEntries(
    Object.entries(row).map(([key, value]) => [
      key,
      value == null ? value : sanitizeProjectStorageText(value),
    ]),
  );

export const normalizeIdentity = (value: unknown): string =>
  normalizeText(value)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

export const normalizePhone = (value: unknown): string => {
  const digits = normalizeText(value).replace(/\D/g, "");
  return digits.length > 10 ? digits.slice(-10) : digits;
};

export const splitEmails = (value: unknown): string[] => [
  ...new Set(
    (
      normalizeText(value).match(
        /[A-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi,
      ) ?? []
    ).map((email) => email.toLowerCase()),
  ),
];

export const splitPhones = (value: unknown): string[] => [
  ...new Set(
    normalizeText(value)
      .split(/[;,|/\r\n]+/)
      .map((phone) => normalizePhone(phone))
      .filter((phone) => phone.length >= 7),
  ),
];

export const isPhoneLikeCustomerName = (value: unknown): boolean => {
  const text = normalizeText(value);
  if (!text) return false;
  const digits = text.replace(/\D/g, "");
  return digits.length >= 7 && digits.length / Math.max(text.length, 1) > 0.45;
};

export const parseProjectDate = (
  value: unknown,
): { value: string | null; invalid: boolean; raw: string } => {
  const raw = normalizeText(value);
  if (NULL_VALUES.has(raw.toLowerCase())) {
    return { value: null, invalid: false, raw };
  }

  if (value instanceof Date) {
    return {
      value: Number.isNaN(value.valueOf())
        ? null
        : value.toISOString().slice(0, 10),
      invalid: Number.isNaN(value.valueOf()),
      raw,
    };
  }

  let year: number;
  let month: number;
  let day: number;
  const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  const isoMatch = raw.match(
    /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T ]\d{1,2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/,
  );
  const monthNameMatch = raw.match(/^([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})$/);

  if (slashMatch) {
    month = Number(slashMatch[1]);
    day = Number(slashMatch[2]);
    year = Number(slashMatch[3]);
    year = year < 100 ? (year < 70 ? 2000 + year : 1900 + year) : year;
  } else if (isoMatch) {
    year = Number(isoMatch[1]);
    month = Number(isoMatch[2]);
    day = Number(isoMatch[3]);
  } else if (monthNameMatch) {
    const parsedMonth = new Date(`${monthNameMatch[1]} 1, 2000`).getMonth();
    year = Number(monthNameMatch[3]);
    month = parsedMonth + 1;
    day = Number(monthNameMatch[2]);
  } else {
    return { value: null, invalid: true, raw };
  }

  const date = new Date(Date.UTC(year, month - 1, day));
  const isValid =
    Number.isFinite(date.valueOf()) &&
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day;
  return {
    value: isValid ? date.toISOString().slice(0, 10) : null,
    invalid: !isValid,
    raw,
  };
};

export const parseProjectMoney = (
  value: unknown,
): { value: number | null; invalid: boolean; raw: string } => {
  const raw = normalizeText(value);
  if (NULL_VALUES.has(raw.toLowerCase())) {
    return { value: null, invalid: false, raw };
  }
  const normalized = raw.replace(/[,$€£\s]/g, "").replace(/^\((.*)\)$/, "-$1");
  const parsed = Number(normalized);
  return {
    value: Number.isFinite(parsed) ? parsed : null,
    invalid: !Number.isFinite(parsed),
    raw,
  };
};

export const parseProjectPercent = (
  value: unknown,
): { value: number | null; invalid: boolean; raw: string } => {
  const raw = normalizeText(value);
  if (NULL_VALUES.has(raw.toLowerCase())) {
    return { value: null, invalid: false, raw };
  }
  const hasPercent = raw.endsWith("%");
  const parsed = Number(raw.replace(/[%\s,]/g, ""));
  if (!Number.isFinite(parsed)) {
    return { value: null, invalid: true, raw };
  }
  return {
    value: hasPercent || Math.abs(parsed) > 1 ? parsed / 100 : parsed,
    invalid: false,
    raw,
  };
};

export const parseProjectBoolean = (
  value: unknown,
): { value: boolean | null; invalid: boolean; raw: string } => {
  const raw = normalizeText(value);
  const lower = raw.toLowerCase();
  if (NULL_VALUES.has(lower)) return { value: null, invalid: false, raw };
  if (["yes", "y", "true", "1"].includes(lower)) {
    return { value: true, invalid: false, raw };
  }
  if (["no", "n", "false", "0"].includes(lower)) {
    return { value: false, invalid: false, raw };
  }
  return { value: null, invalid: true, raw };
};

export type ProjectStageMappingOptions = {
  invoiced?: boolean | null;
  paid?: boolean | null;
};

export const mapStatusToProjectStage = (
  status: unknown,
  options: ProjectStageMappingOptions = {},
): string => {
  const normalized = normalizeText(status).toLowerCase().replace(/[_-]+/g, " ");
  switch (normalized) {
    case "lead":
    case "tire kicker":
    case "new":
    case "opportunity":
    case "":
      return "opportunity";
    case "estimate prepared":
      return "opportunity";
    case "estimate sent":
    case "proposal sent":
    case "in negotiation":
      return "estimate-sent";
    case "sold":
    case "closed won":
    case "won":
      return "in-progress";
    case "in progress":
      return "in-progress";
    case "on hold":
    case "delayed":
      return "on-hold";
    case "completed pending payment":
      return "completed-pending-payment";
    case "completed": {
      const unpaidInvoice =
        (options.paid === false && options.invoiced !== false) ||
        (options.invoiced === true && options.paid == null);
      return unpaidInvoice ? "completed-pending-payment" : "completed";
    }
    case "referral mining":
      return "referral-mining";
    case "closed lost":
    case "lost":
    case "closed-lost":
    case "oos/dq":
    case "oos dq":
    case "oos-dq":
    case "cancelled":
    case "canceled":
      return "closed-lost";
    default:
      return "opportunity";
  }
};

export const validateProjectCsvHeaders = (headers: string[]) => {
  const normalizedHeaders = headers.map((header) => normalizeText(header));
  return PROJECT_CSV_HEADERS.filter(
    (header) => !normalizedHeaders.includes(header),
  );
};

export const parseProjectCsv = (csv: string): ProjectCsvParseResult => {
  const result = Papa.parse<ProjectCsvRow>(csv, {
    header: true,
    skipEmptyLines: "greedy",
    transformHeader: (header) => header.replace(/^\uFEFF/, "").trim(),
  });
  const headers = result.meta.fields ?? [];
  return {
    data: result.data.filter((row) =>
      Object.values(row).some((value) => normalizeText(value) !== ""),
    ),
    headers,
    errors: result.errors.map((error) => ({
      row: error.row,
      message: error.message,
    })),
    missingHeaders: validateProjectCsvHeaders(headers),
  };
};

const getContactEmails = (contact: Contact): string[] =>
  (contact.email_jsonb ?? [])
    .map((entry) => normalizeText(entry?.email).toLowerCase())
    .filter(Boolean);

const getContactPhones = (contact: Contact): string[] =>
  (contact.phone_jsonb ?? [])
    .map((entry) => normalizePhone(entry?.number))
    .filter(Boolean);

const getContactName = (contact: Contact) =>
  normalizeIdentity(`${contact.first_name ?? ""} ${contact.last_name ?? ""}`);

const splitCustomerName = (value: string) => {
  const parts = value.split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { first_name: parts[0] ?? "", last_name: "" };
  return {
    first_name: parts.slice(0, -1).join(" "),
    last_name: parts.at(-1) ?? "",
  };
};

const slugify = (value: string) =>
  normalizeText(value)
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "other";

const issue = (
  rowNumber: number,
  code: ProjectImportIssue["code"],
  message: string,
  field: string | undefined,
  severity: ProjectImportIssue["severity"],
): ProjectImportIssue => ({ rowNumber, code, message, field, severity });

const hasFormulaMismatch = (
  raw: ProjectCsvRow,
  values: Record<string, number | null>,
) => {
  const {
    revenue,
    material,
    labor,
    dump,
    commission,
    other,
    totalCost,
    gross,
    margin,
  } = values;
  const componentValues = [material, labor, dump, commission, other];
  const componentMismatch =
    totalCost != null &&
    componentValues.every((value) => value != null) &&
    Math.abs(
      totalCost - componentValues.reduce((sum, value) => sum + (value ?? 0), 0),
    ) > 0.02;
  const grossMismatch =
    revenue != null &&
    totalCost != null &&
    gross != null &&
    Math.abs(gross - (revenue - totalCost)) > 0.02;
  const marginMismatch =
    revenue != null &&
    revenue !== 0 &&
    gross != null &&
    margin != null &&
    Math.abs(margin - gross / revenue) > 0.02;
  // Keep raw in the signature so callers/tests can verify formulas came from
  // source values even when a component is absent.
  void raw;
  return componentMismatch || grossMismatch || marginMismatch;
};

export const getProjectImportBatchId = (
  filename: string,
  csv: string,
): string => {
  let hash = 2166136261;
  // The content, rather than the local filename, is the idempotency key. A
  // user can download/rename the same source and safely preview it again.
  void filename;
  const value = csv;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `pipeline-${(hash >>> 0).toString(16).padStart(8, "0")}`;
};

export const buildProjectImportPreview = (
  rows: ProjectCsvRow[],
  existingContacts: Contact[] = [],
  existingCompanies: Company[] = [],
  options: ProjectImportPreviewOptions = {},
): { rows: ProjectImportRow[]; summary: ProjectImportSummary } => {
  const contactByEmail = new Map<string, Set<Identifier>>();
  const contactByPhone = new Map<string, Set<Identifier>>();
  const contactByNameAddress = new Map<string, Set<Identifier>>();
  existingContacts.forEach((contact) => {
    getContactEmails(contact).forEach((email) => {
      const ids = contactByEmail.get(email) ?? new Set<Identifier>();
      ids.add(contact.id);
      contactByEmail.set(email, ids);
    });
    getContactPhones(contact).forEach((phone) => {
      const ids = contactByPhone.get(phone) ?? new Set<Identifier>();
      ids.add(contact.id);
      contactByPhone.set(phone, ids);
    });
    const name = getContactName(contact);
    const address = normalizeIdentity(contact.address);
    if (name && address) {
      const ids =
        contactByNameAddress.get(`${name}:${address}`) ?? new Set<Identifier>();
      ids.add(contact.id);
      contactByNameAddress.set(`${name}:${address}`, ids);
    }
  });

  const companyByName = new Map<string, Set<Identifier>>();
  existingCompanies.forEach((company) => {
    const name = normalizeIdentity(company.name);
    if (!name) return;
    const ids = companyByName.get(name) ?? new Set<Identifier>();
    ids.add(company.id);
    companyByName.set(name, ids);
  });

  const sourceKeyCounts = new Map<string, number>();
  const nameAddressCounts = new Map<string, number>();
  const emailCounts = new Map<string, number>();
  const phoneCounts = new Map<string, number>();
  rows.forEach((raw) => {
    const sourceKey = normalizeIdentity(raw["Source Key"]);
    const nameAddress = `${normalizeIdentity(raw["Customer Name"])}:${normalizeIdentity(raw.Address)}`;
    if (sourceKey)
      sourceKeyCounts.set(sourceKey, (sourceKeyCounts.get(sourceKey) ?? 0) + 1);
    if (nameAddress !== ":") {
      nameAddressCounts.set(
        nameAddress,
        (nameAddressCounts.get(nameAddress) ?? 0) + 1,
      );
    }
    splitEmails(raw.Email).forEach((email) =>
      emailCounts.set(email, (emailCounts.get(email) ?? 0) + 1),
    );
    splitPhones(raw.Phone).forEach((phone) =>
      phoneCounts.set(phone, (phoneCounts.get(phone) ?? 0) + 1),
    );
  });

  const existingSourceKeys = new Set(
    (options.existingProjects ?? [])
      .map((project) => normalizeIdentity(project.source_key))
      .filter(Boolean),
  );
  const existingBatchRows = new Set(
    (options.existingProjects ?? [])
      .filter((project) =>
        options.importBatchId
          ? project.import_batch_id === options.importBatchId
          : true,
      )
      .map((project) => project.source_row_number)
      .filter((rowNumber): rowNumber is number => rowNumber != null),
  );

  const byStage: Record<string, number> = {};
  const allIssues: ProjectImportIssue[] = [];
  let contactMatches = 0;
  let newContacts = 0;
  let companyMatches = 0;
  let companyUnmatched = 0;

  const previewRows = rows.map((raw, index) => {
    const rowNumber = index + 2;
    const customerName = normalizeText(raw["Customer Name"]);
    const status = normalizeText(raw.Status);
    const issues: ProjectImportIssue[] = [];

    if (!customerName) {
      issues.push(
        issue(
          rowNumber,
          "missing_customer_name",
          "Customer Name is blank.",
          "Customer Name",
          "error",
        ),
      );
    }
    if (!status) {
      issues.push(
        issue(
          rowNumber,
          "missing_status",
          "Status is blank; the project will start in Opportunity.",
          "Status",
          "warning",
        ),
      );
    }

    const coreReviewFields = [
      [
        "Address",
        raw.Address,
        "missing_site_address",
        "Site address is blank.",
      ],
      ["Job Type", raw["Job Type"], "missing_job_type", "Job Type is blank."],
      ["Source", raw.Source, "missing_lead_source", "Source is blank."],
      [
        "Date of Lead",
        raw["Date of Lead"],
        "missing_lead_date",
        "Date of Lead is blank.",
      ],
      [
        "Source Key",
        raw["Source Key"],
        "missing_source_key",
        "Source Key is blank; row number and batch ID remain the import identity.",
      ],
    ] as const;
    coreReviewFields.forEach(([field, value, code, message]) => {
      if (!normalizeText(value)) {
        issues.push(issue(rowNumber, code, message, field, "warning"));
      }
    });

    const dateFields = [
      ["Removal Date", "removal_date"],
      ["JOB Date", "job_date"],
      ["Reinstall Date", "reinstall_date"],
      ["Invoice Pay Date", "invoice_pay_date"],
      ["Date of Lead", "lead_date"],
    ] as const;
    const dates = Object.fromEntries(
      dateFields.map(([sourceField, projectField]) => {
        const parsed = parseProjectDate(raw[sourceField]);
        if (parsed.invalid) {
          issues.push(
            issue(
              rowNumber,
              "invalid_date",
              `Invalid date: ${parsed.raw}`,
              sourceField,
              "warning",
            ),
          );
        }
        return [projectField, parsed.value];
      }),
    ) as Record<string, string | null>;

    const moneyFields = [
      ["REVENUE/CONTRACT", "contract_amount"],
      ["MATERIAL", "material_cost"],
      ["LABOR", "labor_cost"],
      ["DUMP", "dump_cost"],
      ["COMMISH/MARKETING", "commission_marketing_cost"],
      ["OTHER", "other_cost"],
      ["Total Job Cost", "total_job_cost"],
      ["GROSS", "gross_profit"],
    ] as const;
    const moneyValues: Record<string, number | null> = {};
    const projectMoney: Record<string, number | null> = {};
    moneyFields.forEach(([sourceField, projectField]) => {
      const parsed = parseProjectMoney(raw[sourceField]);
      moneyValues[projectField] = parsed.value;
      projectMoney[projectField] = parsed.value;
      if (parsed.invalid) {
        issues.push(
          issue(
            rowNumber,
            "invalid_currency",
            `Invalid currency: ${parsed.raw}`,
            sourceField,
            "warning",
          ),
        );
      }
    });
    const margin = parseProjectPercent(raw.Margin);
    if (margin.invalid) {
      issues.push(
        issue(
          rowNumber,
          "invalid_percentage",
          `Invalid percentage: ${margin.raw}`,
          "Margin",
          "warning",
        ),
      );
    }
    if (
      hasFormulaMismatch(raw, {
        revenue: moneyValues.contract_amount,
        material: moneyValues.material_cost,
        labor: moneyValues.labor_cost,
        dump: moneyValues.dump_cost,
        commission: moneyValues.commission_marketing_cost,
        other: moneyValues.other_cost,
        totalCost: moneyValues.total_job_cost,
        gross: moneyValues.gross_profit,
        margin: margin.value,
      })
    ) {
      issues.push(
        issue(
          rowNumber,
          "formula_mismatch",
          "Financial values do not reconcile within tolerance.",
          undefined,
          "warning",
        ),
      );
    }

    const invoiced = parseProjectBoolean(raw["Invoiced?"]);
    const paid = parseProjectBoolean(raw["Paid?"]);
    if (invoiced.invalid)
      issues.push(
        issue(
          rowNumber,
          "invalid_boolean",
          `Invalid Yes/No value: ${invoiced.raw}`,
          "Invoiced?",
          "warning",
        ),
      );
    if (paid.invalid)
      issues.push(
        issue(
          rowNumber,
          "invalid_boolean",
          `Invalid Yes/No value: ${paid.raw}`,
          "Paid?",
          "warning",
        ),
      );

    const stage = mapStatusToProjectStage(status, {
      invoiced: invoiced.value,
      paid: paid.value,
    });
    byStage[stage] = (byStage[stage] ?? 0) + 1;

    const emails = splitEmails(raw.Email);
    const phones = splitPhones(raw.Phone);
    const nameKey = normalizeIdentity(customerName);
    const matchingIds = new Set<Identifier>();
    emails.forEach((email) =>
      (contactByEmail.get(email) ?? new Set()).forEach((id) =>
        matchingIds.add(id),
      ),
    );
    phones.forEach((phone) =>
      (contactByPhone.get(normalizePhone(phone)) ?? new Set()).forEach((id) =>
        matchingIds.add(id),
      ),
    );
    if (matchingIds.size === 0 && nameKey && normalizeIdentity(raw.Address)) {
      (
        contactByNameAddress.get(
          `${nameKey}:${normalizeIdentity(raw.Address)}`,
        ) ?? new Set()
      ).forEach((id) => matchingIds.add(id));
    }
    const phoneLikeName = isPhoneLikeCustomerName(customerName);
    if (phoneLikeName) {
      issues.push(
        issue(
          rowNumber,
          "phone_like_customer_name",
          "Customer Name looks like a phone number and needs review.",
          "Customer Name",
          "warning",
        ),
      );
    }
    if (!emails.length && !phones.length) {
      issues.push(
        issue(
          rowNumber,
          "missing_contact_data",
          "No usable email or phone was provided.",
          "Email",
          "warning",
        ),
      );
    }

    let contactMatch: ProjectImportRow["contactMatch"] = "none";
    let contactId: Identifier | undefined;
    let contact: Partial<Contact> | undefined;
    const addressKey = normalizeIdentity(raw.Address);
    const contactKey = emails[0]
      ? `email:${emails[0]}`
      : phones[0]
        ? `phone:${normalizePhone(phones[0])}`
        : nameKey && addressKey
          ? `name-address:${nameKey}:${addressKey}`
          : nameKey
            ? `name-row:${nameKey}:${rowNumber}`
            : undefined;
    if (matchingIds.size === 1 && !phoneLikeName) {
      contactMatch = "matched";
      contactId = [...matchingIds][0];
      contactMatches += 1;
    } else if (matchingIds.size > 1) {
      contactMatch = "ambiguous";
      issues.push(
        issue(
          rowNumber,
          "ambiguous_contact",
          "More than one existing contact matches this row.",
          "Customer Name",
          "warning",
        ),
      );
    } else if (customerName && !phoneLikeName) {
      contactMatch = "new";
      const name = splitCustomerName(customerName);
      contact = {
        ...name,
        address: normalizeText(raw.Address) || undefined,
        email_jsonb: emails.map((email) => ({ email, type: "Work" as const })),
        phone_jsonb: phones.map((number) => ({
          number,
          type: "Work" as const,
        })),
        company_id: undefined,
      };
      newContacts += 1;
    }

    const companyName = normalizeText(raw["Referral/Partner Company"]);
    let companyId: Identifier | undefined;
    if (companyName) {
      const companyIds =
        companyByName.get(normalizeIdentity(companyName)) ??
        new Set<Identifier>();
      if (companyIds.size === 1) {
        companyId = [...companyIds][0];
        companyMatches += 1;
      } else if (companyIds.size > 1) {
        companyUnmatched += 1;
        issues.push(
          issue(
            rowNumber,
            "ambiguous_company",
            `More than one company matches "${companyName}".`,
            "Referral/Partner Company",
            "warning",
          ),
        );
      } else {
        companyUnmatched += 1;
        issues.push(
          issue(
            rowNumber,
            "company_not_found",
            `Referral/Partner Company "${companyName}" was not found.`,
            "Referral/Partner Company",
            "warning",
          ),
        );
      }
    }

    const sourceKey = normalizeText(raw["Source Key"]);
    const nameAddressKey = `${nameKey}:${normalizeIdentity(raw.Address)}`;
    const duplicateCandidate =
      (sourceKey &&
        (sourceKeyCounts.get(normalizeIdentity(sourceKey)) ?? 0) > 1) ||
      (nameAddressKey !== ":" &&
        (nameAddressCounts.get(nameAddressKey) ?? 0) > 1) ||
      emails.some((email) => (emailCounts.get(email) ?? 0) > 1) ||
      phones.some((phone) => (phoneCounts.get(phone) ?? 0) > 1) ||
      (sourceKey && existingSourceKeys.has(normalizeIdentity(sourceKey))) ||
      existingBatchRows.has(rowNumber);
    if (duplicateCandidate) {
      const duplicateFields = [
        sourceKey &&
        ((sourceKeyCounts.get(normalizeIdentity(sourceKey)) ?? 0) > 1 ||
          existingSourceKeys.has(normalizeIdentity(sourceKey)))
          ? "Source Key"
          : null,
        nameAddressKey !== ":" &&
        (nameAddressCounts.get(nameAddressKey) ?? 0) > 1
          ? "Customer Name + Address"
          : null,
        emails.some((email) => (emailCounts.get(email) ?? 0) > 1)
          ? "Email"
          : null,
        phones.some((phone) => (phoneCounts.get(phone) ?? 0) > 1)
          ? "Phone"
          : null,
        existingBatchRows.has(rowNumber) ? "Import batch row" : null,
      ].filter((field): field is string => field != null);
      issues.push(
        issue(
          rowNumber,
          "duplicate_source_identity",
          `A matching source identity already exists in this file or CRM${
            duplicateFields.length ? ` (${duplicateFields.join(", ")})` : ""
          }.`,
          duplicateFields[0] ?? "Source Key",
          "warning",
        ),
      );
    }

    const project: Partial<Project> = {
      name: customerName || `Unnamed project ${index + 1}`,
      company_id: companyId ?? null,
      primary_contact_id: contactId ?? null,
      contact_ids: contactId != null ? [contactId] : [],
      category: slugify(normalizeText(raw["Job Type"])),
      project_type: slugify(normalizeText(raw["Job Type"])),
      stage,
      source_status: status || null,
      lead_source: normalizeText(raw.Source) || null,
      site_address: normalizeText(raw.Address) || null,
      ...dates,
      subcontractor: normalizeText(raw.Sub) || null,
      subcontractor_invoice_reference:
        normalizeText(raw["Sub Inv in shared inv drive?"]) || null,
      quantity: normalizeText(raw.Qty) || null,
      unit: normalizeText(raw.Unit) || null,
      ...projectMoney,
      margin: margin.value,
      invoiced: invoiced.value,
      paid: paid.value,
      calendar_link: normalizeText(raw["Calendar / Link"]) || null,
      source_key: sourceKey || null,
      source_row_number: rowNumber,
      source_payload: sanitizeProjectRawRow(raw),
      raw_source_row: sanitizeProjectRawRow(raw),
      needs_review: issues.length > 0,
      review_reasons: [...new Set(issues.map((item) => item.code))],
      amount: moneyValues.contract_amount ?? 0,
      expected_closing_date: dates.job_date ?? dates.lead_date ?? null,
    };
    allIssues.push(...issues);
    return {
      rowNumber,
      raw,
      project,
      contact,
      companyName: companyName || undefined,
      contactMatch,
      contactId,
      contactKey,
      companyId,
      issues,
      duplicateCandidate: Boolean(duplicateCandidate),
    } satisfies ProjectImportRow;
  });

  const warningRows = previewRows.filter((row) =>
    row.issues.some((item) => item.severity === "warning"),
  ).length;
  const errorRows = previewRows.filter((row) =>
    row.issues.some((item) => item.severity === "error"),
  ).length;
  return {
    rows: previewRows,
    summary: {
      rowCount: previewRows.length,
      validRows: previewRows.length - errorRows,
      warningRows,
      errorRows,
      duplicateRows: previewRows.filter((row) => row.duplicateCandidate).length,
      contactMatches,
      newContacts,
      companyMatches,
      companyUnmatched,
      byStage,
      issues: allIssues,
      importBatchId: options.importBatchId,
    },
  };
};
